// ══════════════════════════════════════════════════════════════════════════════
//  src/components/teacher/ModalAvisarGerencia.jsx
//  EL PROFESOR PIDE UN CAMBIO · por WhatsApp, a gerencia
//
//  Rob: «todo tiene que llegar a mí: si un teacher o un alumno suspende o mueve
//  la clase, todo tiene que pasar por WhatsApp a mí, yo me pongo de acuerdo con
//  ellos para tomar una decisión, y luego yo me meto al CRM y hago los ajustes».
//
//  ── LO QUE ESTE MODAL NO HACE ───────────────────────────────────────────────
//  No toca la agenda. Ni mueve la clase ni la suelta: arma el mensaje y abre
//  WhatsApp. La clase se queda EXACTAMENTE igual hasta que gerencia la ajuste en
//  el CRM. Las rutas que antes lo hacían directo están cerradas en el servidor.
//
//  Sustituye a `ModalPosponer` y `ModalRenunciar`, que aplicaban el cambio sin
//  que el alumno aceptara nada. Esos ficheros se quedan en el repositorio, sin
//  usar.
//
//  ── POR QUÉ SE PUEDE PULSAR AUNQUE FALTEN MENOS DE 24 HORAS ─────────────────
//  Porque ya no cambia nada: solo avisa. Bloquear un aviso sería dejar a gerencia
//  sin enterarse justo de la clase más urgente. Lo que sí hace es decirlo en el
//  mensaje, para que gerencia sepa que ahí aplica lo que dice el contrato.
// ══════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { LIMITES, avisaDelTope } from '../../limites.js';
import { enlaceWhatsApp } from '../../contacto.js';

const MINIMO_MOTIVO = 4;
const HORAS = Array.from({ length: 19 }, (_, i) => i + 5);   // 5:00 a 23:00

export default function ModalAvisarGerencia({
  modo,              // 'mover' | 'no_puedo'
  clase,
  profesor,
  horasMinimas = 24,
  onCerrar,
}) {
  const [motivo, setMotivo] = useState('');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [enviado, setEnviado] = useState(false);

  // El tope vive en `limites.js`, no escrito aquí: repartido por cada modal, el
  // día que se suba uno los demás se quedan viejos.
  const tope  = LIMITES.motivoPosposicion;
  const cerca = avisaDelTope(motivo.length, tope);
  const lleno = motivo.length >= tope;
  // El recorte en el setter, no solo en `maxLength`: es el único punto por el
  // que pasan todos los caminos, también los que entren por programa.
  const escribeMotivo = (t) => setMotivo(String(t).slice(0, tope));

  const esMover = modo === 'mover';
  const tarde = clase.se_puede_posponer === false;
  const limpio = motivo.trim();
  const listo = limpio.length >= MINIMO_MOTIVO;

  const mensaje = useMemo(() => {
    // Sin emojis: WhatsApp los pintaba como un rombo con interrogación
    // (Rob, 11-09-2026). Las negritas y las líneas en blanco sí llegan.
    const lineas = [
      esMover ? '*Un profesor pide mover una clase*' : '*Un profesor no puede dar una clase*',
      '',
      `*Profesor:* ${profesor || 'Profesor'}`,
      `*Alumno:* ${clase.alumno || 'Alumno'}`,
      `*Clase:* ${clase.fecha} · ${clase.hora}:00 CST`,
      esMover && fecha ? `*Propone:* ${fecha}${hora !== '' ? ` · ${hora}:00` : ''}` : null,
      `*Motivo:* ${limpio}`,
      tarde ? `*Aviso con menos de ${horasMinimas} horas*` : null,
      '',
      '_La clase sigue igual hasta que gerencia la ajuste en el CRM._',
    ];
    return lineas.filter((l) => l !== null).join('\n');
  }, [esMover, profesor, clase, fecha, hora, limpio, tarde, horasMinimas]);

  const enlace = enlaceWhatsApp(mensaje);

  // ── EL AVISO QUEDA GUARDADO, NO SOLO ESCRITO EN WHATSAPP (20-09-2026) ──
  // Antes, este botón solo abría WhatsApp: en el sistema no quedaba nada. Si
  // el mensaje no se llegaba a mandar, la clase se quedaba sin profesor y
  // ninguna pantalla de NSL lo sabía. Rob: «de lo que no me entero, no lo sé».
  //
  // Ahora se guarda primero —sale en el radar de gerencia como urgente— y
  // después se abre WhatsApp igual que siempre. La pestaña se abre YA, en el
  // mismo clic: si se abriera al volver del servidor, Safari y los móviles la
  // bloquearían como ventana emergente.
  const [guardando, setGuardando] = useState(false);
  const [fallo, setFallo] = useState(null);

  const envia = async (e) => {
    e.preventDefault();
    if (!listo || guardando) return;
    let pestana = null;
    try { pestana = window.open('', '_blank'); } catch { pestana = null; }
    setGuardando(true);
    setFallo(null);
    try {
      const res = await fetch('/api/teacher/avisos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          modo: esMover ? 'mover' : 'no_puedo',
          alumno: clase.alumno || 'Alumno',
          fecha: String(clase.fecha || '').slice(0, 10),
          hora: Number(clase.hora) || 0,
          motivo: limpio,
          propone_fecha: esMover && fecha ? String(fecha).slice(0, 10) : null,
          propone_hora: esMover && hora !== '' ? Number(hora) : null,
          con_poco_aviso: !!tarde,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail
          : 'No se pudo registrar el aviso.');
      }
    } catch (err) {
      // Si no se pudo guardar, WhatsApp se abre igual: avisar por el camino de
      // siempre es mejor que no avisar. Pero se le dice, para que sepa que en
      // el sistema no consta y lo mencione en el mensaje.
      setFallo(err?.message || 'No se pudo registrar el aviso en NSL.');
    } finally {
      setGuardando(false);
    }
    if (pestana && !pestana.closed) {
      try { pestana.opener = null; pestana.location.href = enlace; }
      catch { window.open(enlace, '_blank', 'noopener,noreferrer'); }
    } else {
      window.open(enlace, '_blank', 'noopener,noreferrer');
    }
    setEnviado(true);
  };

  return (
    <div className="pp-overlay" onClick={onCerrar} role="presentation">
      <div className="pp-modal" onClick={(e) => e.stopPropagation()} role="presentation">
        {enviado ? (
          <>
            <h3 className="pp-modal-t">Mensaje listo para gerencia</h3>
            <p className="pp-modal-s">
              Se abrió WhatsApp con tu mensaje. Envíalo y gerencia se pone de
              acuerdo con tu alumno.
            </p>
            {/* Lo que ahora pasa además de WhatsApp: el aviso consta en NSL. Y
                si no se pudo guardar, se dice —para que lo mencione en el
                mensaje—, en vez de dejarle creer que quedó registrado. */}
            {fallo ? (
              <div className="pp-aviso" data-prueba="aviso-sin-registrar"
                style={{ borderColor: 'rgba(245,166,35,.4)' }}>
                <span aria-hidden="true">⚠️</span>
                <span>
                  <strong>No quedó registrado en NSL</strong> ({fallo}). Asegúrate
                  de enviar el WhatsApp: ahora mismo es la única constancia.
                </span>
              </div>
            ) : (
              <div className="pp-aviso" data-prueba="aviso-registrado">
                <span aria-hidden="true">✅</span>
                <span>
                  <strong>Aviso registrado</strong>: gerencia ya lo tiene en su
                  radar, aunque el WhatsApp tarde en llegar.
                </span>
              </div>
            )}
            <div className="pp-aviso">
              <span aria-hidden="true">📌</span>
              <span>
                <strong>Tu clase sigue igual</strong> en tu agenda hasta que
                gerencia la ajuste. No hace falta que hagas nada más aquí.
              </span>
            </div>
            <p className="pp-nota">
              ¿No se abrió WhatsApp?{' '}
              <a href={enlace} target="_blank" rel="noopener noreferrer">
                Ábrelo desde aquí
              </a>
              .
            </p>
            <div className="pp-acciones">
              <button type="button" className="pp-btn pp-btn-ac" onClick={onCerrar}>
                Cerrar
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={envia}>
            <h3 className="pp-modal-t">
              {esMover ? 'Pedir un cambio de fecha' : 'No puedo dar esta clase'}
            </h3>
            <p className="pp-modal-s">
              {clase.fecha} a las {clase.hora}:00 · {clase.alumno}
            </p>

            <div className="pp-aviso">
              <span aria-hidden="true">💬</span>
              <span>
                Esto <strong>no cambia tu clase</strong>. Le llega a gerencia por
                WhatsApp, gerencia habla con tu alumno y hace el ajuste.
              </span>
            </div>

            {tarde && (
              <div className="pp-aviso">
                <span aria-hidden="true">⚠️</span>
                <span>
                  Faltan menos de <strong>{horasMinimas} horas</strong>. Gerencia
                  recibe el aviso igual, y aplica lo que dice tu acuerdo.
                </span>
              </div>
            )}

            {esMover && (
              <div className="pp-campo">
                <label className="pp-label" htmlFor="ag-fecha">
                  ¿Qué fecha propones? <i>(opcional)</i>
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    id="ag-fecha"
                    className="pp-input"
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                  />
                  <select
                    className="pp-input"
                    value={hora}
                    onChange={(e) => setHora(e.target.value)}
                    aria-label="Hora que propones"
                  >
                    <option value="">Hora</option>
                    {HORAS.map((h) => (
                      <option key={h} value={h}>{h}:00</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="pp-campo">
              <label className="pp-label" htmlFor="ag-motivo">
                {esMover ? '¿Por qué hay que moverla?' : '¿Por qué no puedes?'}
              </label>
              <textarea
                id="ag-motivo"
                className="pp-area"
                rows={3}
                maxLength={tope}
                value={motivo}
                onChange={(e) => escribeMotivo(e.target.value)}
                placeholder="Gerencia lo necesita para hablar con tu alumno."
              />
              {/* El contador siempre a la vista: un límite que no se ve antes de
                  chocar con él no está puesto para quien escribe. */}
              <span
                className={`pp-contador${lleno ? ' pp-tope' : cerca ? ' pp-cerca' : ''}`}
              >
                {motivo.length} / {tope}
              </span>
            </div>

            <div className="pp-acciones">
              <button type="submit" className="pp-btn pp-btn-ac" disabled={!listo || guardando}
                data-prueba="avisar-enviar">
                Enviar por WhatsApp
              </button>
              <button type="button" className="pp-btn" onClick={onCerrar}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
